import { useCallback, useEffect, useState } from 'react';
import { useUrlParam, useUrlPanelSync } from '../../../../hooks/useUrlParam';
import { useUser } from '../../../../context/UserContext';
import { supabase } from '../../../../lib/supabase';
import { deleteCompetency, fetchCompetencies, PROOF_KINDS, saveCompetency, type Competency, type Proof, type ProofKind } from '../../../../lib/offers';
import { Badge, Button, Card, Dropdown, IconButton, Input, SidePanel, useToast } from '../../../ui';
import { textareaStyle } from './styles';

const faint: React.CSSProperties = { color: 'var(--text-faint)' };

type Draft = { id?: string; name: string; description: string; proof: Proof[] };
const EMPTY: Draft = { name: '', description: '', proof: [] };

/**
 * Client detail → Knowledge → Competencies: what the business can do, each with the proof that it can.
 * Offers are built from these, and every money page draws its evidence from the proof, so a competency
 * with no proof is shown as one.
 */
export default function CompetenciesCard({ client }: { client: { id: string; name: string } }) {
  const { user } = useUser();
  const toast = useToast();
  const [rows, setRows] = useState<Competency[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [draft, setDraft] = useState<Draft | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      setRows(await fetchCompetencies(client.id));
    } catch (err) {
      console.error('Error loading competencies:', err);
    } finally {
      setIsLoading(false);
    }
  }, [client.id]);

  useEffect(() => {
    void load();
    const channel = supabase
      .channel(`competencies-${client.id}`)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'client_competencies', filter: `client_id=eq.${client.id}` }, () => void load())
      .subscribe();
    return () => {
      supabase.removeChannel(channel);
    };
  }, [client.id, load]);

  // The open competency is ?competency=<id>; adding a new one stays local.
  const [urlCompetency, setUrlCompetency] = useUrlParam('competency');
  useUrlPanelSync(
    urlCompetency,
    draft?.id ?? null,
    !isLoading,
    (id) => {
      const c = rows.find((r) => r.id === id);
      if (c) setDraft({ id: c.id, name: c.name, description: c.description, proof: c.proof });
    },
    () => setDraft(null),
  );
  const closeDraft = () => {
    setDraft(null);
    if (urlCompetency) setUrlCompetency(null);
  };

  const setProof = (i: number, patch: Partial<Proof>) =>
    draft && setDraft({ ...draft, proof: draft.proof.map((p, j) => (j === i ? { ...p, ...patch } : p)) });

  const save = async () => {
    if (!draft) return;
    setIsSaving(true);
    try {
      await saveCompetency(client.id, { ...draft, proof: draft.proof.filter((p) => p.text.trim()) }, user?.id);
      closeDraft();
      await load();
    } catch (err) {
      toast.error('Couldn’t save the competency', err instanceof Error ? err.message : 'Nothing was saved.');
    } finally {
      setIsSaving(false);
    }
  };

  const remove = async () => {
    if (!draft?.id) return;
    setIsSaving(true);
    try {
      await deleteCompetency(draft.id);
      closeDraft();
      await load();
    } catch (err) {
      toast.error('Couldn’t delete it', err instanceof Error ? err.message : 'Nothing changed.');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <>
      <Card
        eyebrow="Knowledge"
        title={isLoading ? 'Competencies' : `${rows.length} competenc${rows.length === 1 ? 'y' : 'ies'}`}
        actions={
          <Button variant="secondary" size="sm" icon="plus" onClick={() => setDraft({ ...EMPTY })}>
            Add competency
          </Button>
        }
      >
        {!isLoading && rows.length === 0 ? (
          <div style={{ padding: '20px 0', textAlign: 'center', fontSize: 'var(--text-sm)', color: 'var(--text-muted)' }}>
            What {client.name} is good at, each with its proof. Offers are built from these; an offer ideation fills gaps it can source.
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            {rows.map((c) => (
              <button
                key={c.id}
                type="button"
                className="hover-wash"
                onClick={() => setUrlCompetency(c.id)}
                style={{ all: 'unset', cursor: 'pointer', display: 'flex', alignItems: 'baseline', gap: '10px', padding: '8px 10px', margin: '0 -10px', borderRadius: 'var(--radius-sm)' }}
              >
                <span style={{ fontSize: 'var(--text-sm)', color: 'var(--text-body)', fontWeight: 'var(--weight-medium)' as React.CSSProperties['fontWeight'] }}>{c.name}</span>
                <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: 'var(--text-xs)', ...faint }}>{c.description}</span>
                {c.proof.length ? <Badge tone="up">{c.proof.length} proof</Badge> : <Badge tone="warn">no proof</Badge>}
              </button>
            ))}
          </div>
        )}
      </Card>

      <SidePanel
        open={!!draft}
        onClose={closeDraft}
        width={560}
        eyebrow="Competency"
        title={draft?.id ? draft.name || 'Competency' : 'Add a competency'}
        footer={
          <div style={{ display: 'flex', gap: '8px' }}>
            {draft?.id ? (
              <Button variant="danger" onClick={remove} disabled={isSaving}>
                Delete
              </Button>
            ) : null}
            <span style={{ flex: 1 }} />
            <Button variant="ghost" onClick={closeDraft} disabled={isSaving}>
              Cancel
            </Button>
            <Button variant="primary" onClick={save} disabled={isSaving || !draft?.name.trim()}>
              {isSaving ? 'Saving...' : 'Save'}
            </Button>
          </div>
        }
      >
        {draft ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)', fontSize: 'var(--text-sm)' }}>
            <label style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
              <span className="t-eyebrow">Competency</span>
              <Input value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })} placeholder="e.g. Emergency electrical repair" />
            </label>
            <label style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
              <span className="t-eyebrow">What it covers</span>
              <textarea value={draft.description} onChange={(e) => setDraft({ ...draft, description: e.target.value })} rows={3} style={textareaStyle} placeholder="What they do, for whom, and how well." />
            </label>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              <span className="t-eyebrow">Proof</span>
              <span style={{ ...faint, fontSize: 'var(--text-xs)' }}>Projects, reviews, photos, numbers, credentials. Money pages draw their evidence from here.</span>
              {draft.proof.map((p, i) => (
                <div key={i} style={{ display: 'grid', gridTemplateColumns: '120px minmax(0, 1fr) auto', gap: '6px', alignItems: 'start' }}>
                  <Dropdown
                    size="sm"
                    value={p.kind}
                    onChange={(e) => setProof(i, { kind: e.target.value as ProofKind })}
                    options={PROOF_KINDS.map((k) => ({ value: k.value, label: k.label }))}
                  />
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                    <Input fieldSize="sm" value={p.text} onChange={(e) => setProof(i, { text: e.target.value })} placeholder="What it shows" />
                    <Input fieldSize="sm" value={p.url ?? ''} onChange={(e) => setProof(i, { url: e.target.value || undefined })} placeholder="Link (optional)" />
                  </div>
                  <IconButton icon="x" size="sm" aria-label="Remove proof" onClick={() => setDraft({ ...draft, proof: draft.proof.filter((_, j) => j !== i) })} />
                </div>
              ))}
              <div>
                <Button variant="ghost" size="sm" icon="plus" onClick={() => setDraft({ ...draft, proof: [...draft.proof, { kind: 'project', text: '' }] })}>
                  Add proof
                </Button>
              </div>
            </div>
          </div>
        ) : null}
      </SidePanel>
    </>
  );
}
